import type { Table } from "@tanstack/react-table";
import { Settings2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Yashirib bo'lmaydigan ustunlar: ularsiz qator o'qilmaydi yoki boshqarilmaydi. */
const LOCKED = ["actions", "index", "select"];

const storagePath = (key: string) => `qahvadosh.table-columns.${key}`;

interface Props<TData> {
  table: Table<TData>;
  /** Tanlov shu kalit ostida saqlanadi — har sahifada o'ziniki. */
  storageKey: string;
}

/**
 * Ustunlarni yashirish.
 *
 * Tanlov `localStorage` da: bu foydalanuvchining ish o'rni sozlamasi,
 * server ma'lumoti emas — va uni har kirishda qaytadan qilish kerak
 * bo'lsa, imkoniyatning ma'nosi qolmaydi. URL'ga ham yozilmaydi:
 * hamkasbga tashlangan havola uning o'z ustunlarini almashtirib
 * yubormasligi kerak.
 */
export function ColumnVisibilityControl<TData>({ table, storageKey }: Readonly<Props<TData>>) {
  const { t } = useTranslation();
  const visibility = table.getState().columnVisibility;
  const hydrated = useRef(false);

  // Saqlangan tanlovni bir marta tiklaymiz.
  useEffect(() => {
    const saved = localStorage.getItem(storagePath(storageKey));
    if (!saved) return;

    try {
      table.setColumnVisibility(JSON.parse(saved) as Record<string, boolean>);
    } catch {
      // Buzilgan yozuv — jimgina tashlab yuboriladi. Foydalanuvchiga
      // xato ko'rsatish bu yerda ortiqcha: u hech narsa qilmagan va
      // tuzata ham olmaydi.
      localStorage.removeItem(storagePath(storageKey));
    }
  }, [storageKey, table]);

  // BIRINCHI render'da yozilmaydi: o'shanda holat hali bo'sh, va uni
  // saqlash yuqoridagi tiklashdan oldin ishga tushib, saqlangan
  // tanlovni o'chirib yuborardi. Bayroq `ref` da — uni o'zgartirish
  // qayta render talab qilmaydi.
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }

    localStorage.setItem(storagePath(storageKey), JSON.stringify(visibility));
  }, [visibility, storageKey]);

  const columns = table.getAllLeafColumns().filter(column => !LOCKED.includes(column.id));
  const hidden = columns.filter(column => !column.getIsVisible()).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative" aria-label={t("table.columns")}>
          <Settings2 className="size-4" />
          {/* Yashirilgan ustun borligini tugmaning o'zi aytadi — aks
              holda "ustun qayerga ketdi?" degan savol paydo bo'ladi. */}
          {hidden > 0 && (
            <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 grid size-4 place-items-center rounded-full text-[10px] tabular-nums">
              {hidden}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-52 gap-1 p-1">
        {columns.map(column => {
          const header = column.columnDef.header;

          return (
            <Label
              key={column.id}
              className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-normal"
            >
              <Checkbox
                checked={column.getIsVisible()}
                onCheckedChange={checked => column.toggleVisibility(Boolean(checked))}
              />
              <span className="truncate">{typeof header === "string" ? header : column.id}</span>
            </Label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
